import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import DeviceStack from '../../../components/common/DeviceStack';
import PhoneScreenshot from '../../../components/common/PhoneScreenshot';
import { AuthContext } from '../../../context/AuthProvider';

const Hero = ({ 
  title, 
  subtitle1,
  subtitle2,
  stats, 
  appIcon, 
  // New props for multi-device display
  phoneImage,
  tabletImage,
  webImage,
  // Legacy props (kept for backwards compatibility)
  heroImage, 
  downloadLinks,
  theme,
  heroImageDark,
  currentTheme
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
    },
    
    headline: {
        color: theme?.text?.primary,
        textTransform: 'uppercase',
    },
    subtitle: {
      color: theme?.text?.secondary,
      lineHeight: '1.8rem'
    },
    primaryButton: {
      background: theme?.primary?.blue?.[500],
      color: theme?.neutral?.absolute_white,
    },
    buttonHover: {
      transform: 'scale(1.02)',
      transition: 'all 0.2s ease',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    },
    secondaryButton: {
      borderColor: theme?.primary?.blue?.[500],
      color: theme?.primary?.blue?.[500]
    },
    secondaryButtonHover: {
      backgroundColor: theme?.primary?.blue?.[500],
      color: theme?.neutral?.absolute_white
    },
    statisticsNumber: {
      color: theme?.primary?.blue?.[500]
    },
    statisticsLabel: {
      color: theme?.text?.secondary
    },
  };
  
  // Determine if we should show multi-device or single device
  const showMultiDevice = phoneImage && tabletImage && webImage;

  return (
    <section id="home" style={styles.container}>
      <div className="relative container mx-auto px-4 pt-8">
        {/* Breadcrumb Component */}
        <Breadcrumb theme={theme} />

        <div className="flex flex-col gap-6 sm:gap-8 lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <div className={`space-y-4 sm:space-y-6 lg:space-y-8 ${isVisible ? 'animate-fade-in' : ''} lg:order-1`}>
            {/* App Icon above Headline */}
            {appIcon && (
              <div className="flex justify-center lg:justify-start mb-2 sm:mb-4 lg:mb-6">
                <img
                  src={appIcon}
                  alt="App Icon"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl shadow-lg"
                />
              </div>
            )}

            {/* Headline */}
            <div className="space-y-3 sm:space-y-4 lg:space-y-6">
              <h1
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-snug text-center lg:text-left"
                style={styles.headline}
              >
                {title}
              </h1>

              <p
                className="text-base sm:text-lg md:text-xl leading-relaxed font-light max-w-2xl text-center lg:text-left mx-auto lg:mx-0"
                style={styles.subtitle}
              >
                {subtitle1}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="mt-6 sm:mt-8 md:mt-12 flex justify-center lg:justify-start px-4 sm:px-0">
              <button
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-xl hover:scale-[1.02] hover:shadow-lg transition-all duration-200"
                style={{...styles.primaryButton, minHeight: '48px'}}
                onClick={() => navigate(isAuthenticated ? '/home' : '/welcome')}
              >
                {isAuthenticated ? 'Go to Exam Prep' : 'Take Free Diagnostic Test'}
              </button>
            </div>
          </div>

          {/* Right Column - Device Stack */}
          <div className="w-full flex justify-center lg:justify-end mt-2 sm:mt-4 lg:mt-0 lg:order-2">
            {showMultiDevice ? (
              <DeviceStack
                phoneImage={phoneImage}
                tabletImage={tabletImage}
                webImage={webImage}
                theme={theme}
              />
            ) : (
              // Fallback to legacy single phone display if multi-device images not provided
              <PhoneScreenshot
                imageSrc={currentTheme === 'dark' ? heroImageDark : heroImage}
                alt="App Screenshot"
                theme={theme}
                showGlow={true}
              />
            )}
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default Hero;